/*
 * footer-inova — the behaviour of inova's footer, copied from its theme.js (refs/inova/footer/theme.js):
 *   FooterParallax  div is="footer-parallax": translateY(-50%) -> translateY(0) while the element enters
 *                   (Motion.scroll, offset Enter = its top at the viewport bottom .. its bottom at the viewport bottom);
 *                   off on touch and below 768 (mobileDisabled), and with reduced motion.
 *   ParallaxOverlay parallax-overlay data-target=height data-start=100% data-stop=0%, same offset, target = the panel.
 *   FooterGroup     the section before the footer gets .section--next-rounded (rounded bottom corners).
 *   footer-details  accordions below 768 (animated like AccordionDetails.transition), open (and not clickable) from 768.
 * Ours reads layout boxes, not transformed ones: the offset is measured on the untransformed position, as Motion does.
 * The desktop theme scrolls inside .page-wrapper (base.css:36), not the window - so scroll is listened to in capture.
 * Theme 104.0, 29.9.2026.
 */
(() => {
  const mqDesk = window.matchMedia('(min-width: 768px)');
  const mqFine = window.matchMedia('(pointer: fine)');
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  // --- magnet (F1, Theme 135.0, head 66.0): the source's magnet-link / magnet-button in its footer - the phone / email links (the inner
  // text span moves, the link keeps its underline), the socials (strength 10), the subscribe button (data-magnet 20). Measured on the
  // source: review-135/ft/mag/ (quarter offsets settle +-2.5 / +-5.2). The spring = the H1 copy in sections/header.liquid (bundle-cart.js
  // 131.0) verbatim, with the strength and the moving element as arguments. any-hover only; not touch, not reduced motion.
  const MAGNET = [['.finv__contact-link', '.finv__contact-text', 10], ['.finv__social-link', null, 10], ['.finv__nl-submit', null, 20]];
  const magnet = (el, mover, strength) => {
    if (!window.matchMedia('(any-hover: hover)').matches) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const spring = (from, to, v0) => {
      const o = v0 ? v0 / 1000 : 0; const l = to - from; const w = Math.sqrt(100) / 1000; const z = 10 / (2 * Math.sqrt(100));
      const wd = w * Math.sqrt(1 - z * z);
      const h = (s) => to - Math.exp(-z * w * s) * (((z * w * l - o) / wd) * Math.sin(wd * s) + l * Math.cos(wd * s));
      const vel = (s) => { const q = Math.max(s - 5, 0); return s - q ? (h(s) - h(q)) * (1000 / (s - q)) : 0; };
      const kf = [h(0)];
      let s = 10;
      let done = Math.abs(o) <= 2 && Math.abs(to - h(0)) <= 0.5;
      while (!done && s < 10000) {
        const cur = h(s);
        done = Math.abs(vel(s)) <= 2 && Math.abs(to - cur) <= 0.5;
        kf.push(done ? to : cur);
        s += 10;
      }
      if (kf.length === 1) kf.push(h(0));
      return { h, vel, kf, dur: (kf.length - 1) * 10 };
    };
    const at = (g, s) => {
      if (s >= g.dur) return g.kf[g.kf.length - 1];
      const i = Math.floor(s / 10); const f = (s - i * 10) / 10;
      return g.kf[i] + (g.kf[i + 1] - g.kf[i]) * f;
    };
    const ax = [{ g: null, start: 0, v: 0 }, { g: null, start: 0, v: 0 }];
    let raf = 0;
    const paint = (now) => {
      let running = false;
      for (const a of ax) {
        if (!a.g) continue;
        const s = Math.max(0, now - a.start);
        a.v = at(a.g, s);
        if (s < a.g.dur) running = true;
      }
      mover.style.transform = (ax[0].v || ax[1].v) ? `translate(${ax[0].v.toFixed(3)}px, ${ax[1].v.toFixed(3)}px)` : '';
      raf = running ? requestAnimationFrame(paint) : 0;
    };
    const aim = (tx, ty) => {
      const tl = document.timeline.currentTime;
      [tx, ty].forEach((to, i) => {
        const a = ax[i];
        let from = 0;
        let v0 = 0;
        if (a.g) {
          const e = Math.max(0, tl - a.start);
          if (e >= a.g.dur) from = a.g.kf[a.g.kf.length - 1];
          else { from = a.g.h(e); v0 = a.g.vel(e); }
        }
        a.g = spring(from, to, v0);
        a.start = tl;
      });
      if (!raf) raf = requestAnimationFrame(paint);
    };
    el.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'touch' || reduced.matches) return;
      // 218.0: (clientX - rect) is DRAWN px, offsetWidth / Height LAYOUT px - / the link's zoom (MDN Element.currentCSSZoom; 1 below
      // 1280 = today's numbers) - the hotspot-linea 4ad56cd0 helper. Measured (218.0 zb/magnet_r.mjs): at 1518 the social link pulled
      // +0.85 at 0.9 of its width (designed +4) and -1.75 up at mid-height (designed 0); 2335 = designed.
      const r = el.getBoundingClientRect(), z = el.currentCSSZoom || 1;
      aim(((e.clientX - r.left) / z / el.offsetWidth - 0.5) * strength, ((e.clientY - r.top) / z / el.offsetHeight - 0.5) * strength);
    });
    el.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'touch' || reduced.matches) return;
      aim(0, 0);
    });
  };

  function init(host) {
    if (host.finvReady) return;
    host.finvReady = true;
    const section = host.closest('.shopify-section');
    const movers = Array.from(host.querySelectorAll('[data-finv-parallax]'));
    const panel = host.querySelector('.finv');
    const overlay = host.querySelector('[data-finv-overlay]');
    const details = Array.from(host.querySelectorAll('[data-finv-details]'));

    // --- the newsletter's error line (F2, Theme 135.0, head 66.0): the source's theme.formValidation (theme.js), footer form only.
    // 'invalid' -> no browser bubble; the line under the field (texts = section settings), aria-invalid + describedby; then focus the
    // first invalid field, or announce the text when it already had focus. A field that becomes valid while typing clears its line.
    const nlForm = host.querySelector('.finv__nl-form');
    if (nlForm && !nlForm.finvValid) {
      nlForm.finvValid = true;
      const textFor = (f) => {
        const box = f.closest('[data-finv-err-required]');
        if (f.type === 'email' && f.validity.valueMissing && box && box.dataset.finvErrRequired) return box.dataset.finvErrRequired;
        if (f.type === 'email' && f.validity.typeMismatch && box && box.dataset.finvErrInvalid) return box.dataset.finvErrInvalid;
        return f.validationMessage;
      };
      const lineFor = (f) => {
        const box = f.closest('.finv__field') || f.parentElement;
        let p = box.querySelector(':scope > .finv__nl-error');
        if (!p) {
          p = document.createElement('p');
          p.className = 'finv__nl-error';
          p.id = `${f.id}-error`;
          p.hidden = true;
          box.append(p);
        }
        return p;
      };
      const describedBy = (f, id, add) => {
        const ids = (f.getAttribute('aria-describedby') || '').split(/\s+/).filter((t) => t && t !== id);
        if (add) ids.push(id);
        return ids.join(' ');
      };
      const flag = (f) => {
        const p = lineFor(f);
        p.textContent = textFor(f);
        p.hidden = false;
        f.setAttribute('aria-invalid', 'true');
        f.setAttribute('aria-describedby', describedBy(f, p.id, true));
      };
      const clear = (f) => {
        if (f.getAttribute('aria-invalid') !== 'true') return;
        const p = lineFor(f);
        p.hidden = true;
        f.removeAttribute('aria-invalid');
        const d = describedBy(f, p.id, false);
        if (d) f.setAttribute('aria-describedby', d); else f.removeAttribute('aria-describedby');
      };
      const announce = (text) => {
        let r = nlForm.querySelector(':scope > .finv__nl-announcer');
        if (!r) {
          r = document.createElement('p');
          r.className = 'finv__nl-announcer';
          r.setAttribute('role', 'status');
          nlForm.prepend(r);
        }
        r.textContent = '';
        setTimeout(() => { r.textContent = text; }, 100);
      };
      let timer = 0;
      nlForm.addEventListener('invalid', (e) => {
        const f = e.target;
        e.preventDefault();
        flag(f);
        clearTimeout(timer);
        timer = setTimeout(() => {
          const bad = Array.from(nlForm.elements).filter((x) => x.matches('input, select, textarea') && x.willValidate && !x.validity.valid);
          if (!bad.length) return;
          const before = document.activeElement;
          for (const x of bad) { x.focus(); if (document.activeElement === x) break; }
          if (document.activeElement === before) announce(lineFor(bad.includes(before) ? before : bad[0]).textContent);
        }, 0);
      }, true);
      const onChange = (e) => {
        const f = e.target;
        if (!f.matches || !f.matches('input, select, textarea') || !f.validity.valid) return;
        clear(f);
      };
      nlForm.addEventListener('input', onChange);
      nlForm.addEventListener('change', onChange);
    }

    // --- #14 the submit's busy state (Theme 174.0, Head 79.0): the source hover-button (refs/inova/footer/theme.js), read on the source
    // at 1440 + 390 with every POST aborted (review-174/nb/out/busy-ref.json): `submit` -> aria-busy; the arrow opacity -> 0 (ease 150 ms),
    // then the loader -> 1 (ease 150 ms); the dots scale 1.6 <-> 0.6, 350 ms alternate ease, infinite, stagger 175 ms. The button is not
    // disabled. Set on EVERY submit event, as the source: Shopify's hCaptcha script prevents the event and re-submits the form itself
    // (who-prevents.json, both sides), so `defaultPrevented` cannot gate it; an invalid address fires `invalid`, never `submit`. A 2nd submit
    // restarts the dots, one loop. `pageshow` (back from the bfcache) -> busy off: the dots stop, the loader -> 0, then the arrow -> 1.
    // Reduced motion (Elad 10.10 Q1 "ב"): the opacities jump, no dots loop. No CSS hidden state: without this script the arrow stays.
    const nlBtn = nlForm && nlForm.querySelector('.finv__nl-submit');
    const nlLoader = nlBtn && nlBtn.querySelector('.finv__nl-loader');
    const nlArrow = nlBtn && nlBtn.querySelector(':scope > svg');
    if (nlBtn && nlLoader && nlArrow && typeof nlBtn.animate === 'function' && !nlForm.finvBusy) {
      nlForm.finvBusy = true;
      let fades = [];
      let dots = [];
      const swap = (out, inn) => {
        const reduced = mqReduce.matches;
        const from = [getComputedStyle(out.el).opacity, getComputedStyle(inn.el).opacity];   // read before the cancel: what is on screen
        fades.forEach((f) => f.cancel());
        fades = [
          out.el.animate([{ opacity: from[0] }, { opacity: out.to }], { duration: reduced ? 0 : 150, easing: 'ease', fill: 'forwards' }),
          inn.el.animate([{ opacity: from[1] }, { opacity: inn.to }], { duration: reduced ? 0 : 150, delay: reduced ? 0 : 150, easing: 'ease', fill: 'both' }),
        ];
        return fades;
      };
      nlForm.addEventListener('submit', () => {
        nlBtn.setAttribute('aria-busy', 'true');
        swap({ el: nlArrow, to: 0 }, { el: nlLoader, to: 1 });
        dots.forEach((d) => d.cancel());
        dots = mqReduce.matches ? [] : Array.from(nlLoader.children).map((d, i) => d.animate(
          [{ transform: 'scale(1.6)' }, { transform: 'scale(0.6)' }],
          { duration: 350, delay: i * 175, direction: 'alternate', iterations: Infinity, easing: 'ease', fill: 'both' },
        ));
      });
      window.addEventListener('pageshow', () => {
        if (nlBtn.getAttribute('aria-busy') !== 'true') return;
        nlBtn.removeAttribute('aria-busy');
        dots.forEach((d) => d.cancel());
        dots = [];
        const run = swap({ el: nlLoader, to: 0 }, { el: nlArrow, to: 1 });
        Promise.all(run.map((f) => f.finished)).then(() => { if (fades === run) { run.forEach((f) => f.cancel()); fades = []; } }, () => {});
      });
    }

    // magnet (F1): bound on the first pointerover, so a re-rendered section binds again
    host.addEventListener('pointerover', (e) => {
      if (!(e.target instanceof Element)) return;
      for (const [sel, inner, strength] of MAGNET) {
        const el = e.target.closest(sel);
        if (!el || !host.contains(el) || el.finvMagnet) continue;
        el.finvMagnet = true;
        magnet(el, (inner && el.querySelector(inner)) || el, strength);
      }
    });

    // --- accordions (source footer-details): closed below 768, open from 768
    // Below 768 a tap animates like the source's AccordionDetails.transition (refs/inova/footer/theme.js): open = height
    // summary -> full 250ms ease, the links fade in and rise 10px (150ms, from +150ms); close = the links fade out (150ms) and the
    // height returns to the summary (250ms, same start), then `open` is removed. Measured on the source at 390 (theme 106.0,
    // review-105/acc-probe-ref-106.txt). The source animates under prefers-reduced-motion too - NOT copied: Elad 10.10 Q1 "ב"
    // (Theme 173.0) - with reduced motion the column jumps open / closed (mqReduce read at each tap, so a setting change applies).
    // Deviation (a source bug, not copied - head 60.0): a running animation is cancelled and restarted from the height on
    // screen, so the last tap always wins; the source loses a tap made while it is closing (acc-probe-ref-106.txt, case C:
    // the reopen runs to 203, then the old close removes `open`). The state is read from `open` on every tap, never a flag.
    const anims = new Map();   // details -> { want, list }
    const stop = (d) => {
      const run = anims.get(d);
      if (!run) return;
      anims.delete(d);
      run.list.forEach((a) => a.cancel());
      d.style.overflow = '';
      d.removeAttribute('data-finv-closing');
    };
    const syncDetails = () => details.forEach((d) => { stop(d); d.open = mqDesk.matches; });
    syncDetails();
    mqDesk.addEventListener('change', syncDetails);

    const animate = (d, want) => {
      const summary = d.querySelector('summary');
      const content = summary && summary.nextElementSibling;
      const from = d.getBoundingClientRect().height;   // read BEFORE cancelling: the height on screen right now
      const fade = content ? getComputedStyle(content).opacity : '1';   // same - after the cancel it reads 1 (a flash)
      stop(d);
      if (!summary || !content || typeof d.animate !== 'function' || mqReduce.matches) { d.open = want; return; }
      d.style.overflow = 'hidden';
      if (want) d.open = true;
      else d.setAttribute('data-finv-closing', '');   // Theme 207.0: the chevron turns at the tap, like the source (203.0's accordion)
      // the animated height is a border box (base.css `* { box-sizing: border-box }`, and .finv__col has a 1px top border);
      // scrollHeight and the summary's height leave the border out - without it the end snaps by 1px (head 60.0 (b))
      const borders = d.offsetHeight - d.clientHeight;
      const to = (want ? d.scrollHeight : summary.offsetHeight) + borders;
      const list = want
        ? [
          d.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: 'ease' }),
          content.animate({ opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 150, delay: 150, easing: 'ease', fill: 'backwards' }),
        ]
        : [
          content.animate({ opacity: [fade, 0] }, { duration: 150, easing: 'ease', fill: 'forwards' }),
          d.animate({ height: [`${from}px`, `${to}px`] }, { duration: 250, easing: 'ease' }),
        ];
      const run = { want, list };
      anims.set(d, run);
      const height = want ? list[0] : list[1];         // the 250ms height animation ends the transition
      height.finished.then(() => {
        if (anims.get(d) !== run) return;              // cancelled by a newer tap
        if (!want) d.open = false;
        stop(d);                                        // also drops the content's fill
      }, () => {});
    };

    details.forEach((d) => {
      const summary = d.querySelector('summary');
      if (!summary) return;
      summary.addEventListener('click', (e) => {
        if (mqDesk.matches) return;                     // from 768 the summaries are pointer-events none, as the source
        e.preventDefault();
        const run = anims.get(d);
        animate(d, !(run ? run.want : d.open));
      });
    });

    // --- localization: the source submits on change
    host.querySelectorAll('[data-finv-loc]').forEach((sel) => {
      sel.addEventListener('change', () => sel.form && sel.form.submit());
    });

    // --- the section above gets rounded bottom corners (source FooterGroup / .section--next-rounded)
    // Only a section INSIDE the footer group, like the source rule `.footer-group .section--next-rounded`
    // (refs/inova/footer/src-footer-rules.css:19). No fallback to a template section: .finv-prev-rounded carries
    // overflow: clip, and on a <main> section (product media, popovers) that clip would cut real content.
    // If the group has no visible section above the footer, nothing is rounded - as in the source.
    if (section) {
      let prev = section.previousElementSibling;
      while (prev && (!prev.classList.contains('shopify-section') || prev.offsetHeight === 0)) prev = prev.previousElementSibling;
      if (prev && !prev.classList.contains('shopify-section-group-footer-group')) prev = null;
      if (prev) {
        // the corners only show if the rounded box has a painted background: take the first one painted inside it
        let paint = '';
        const walk = (el, depth) => {
          if (paint || !el || depth > 4) return;
          const bg = getComputedStyle(el).backgroundColor;
          if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') { paint = bg; return; }
          walk(el.firstElementChild, depth + 1);
        };
        walk(prev, 0);
        if (!paint) paint = getComputedStyle(document.body).backgroundColor;
        prev.style.backgroundColor = paint;
        prev.classList.add('finv-prev-rounded');
      }
    }

    // --- parallax (source FooterParallax + ParallaxOverlay)
    const active = () => mqDesk.matches && mqFine.matches && !mqReduce.matches;
    let raf = 0;
    // 218.0: LAYOUT px throughout - the rect top and innerHeight are DRAWN px under the site zoom (measured at 1518: innerHeight
    // 730 while the page's own window is 1123), offsetHeight and the translate are LAYOUT px; drawn / the element's zoom (MDN
    // Element.currentCSSZoom; 1 below 1280) - the hotspot-linea 4ad56cd0 helper. Measured (Home 24 TABLE24 #1): at the page end
    // translateY -114.79 + overlay 28.85 % at 1518 vs 0 / 0 % designed.
    const zf = (el) => el.currentCSSZoom || 1;
    const layoutTop = (el, t) => el.getBoundingClientRect().top / zf(el) - t; // undo our own translate
    const progress = (top, height, vh) => Math.min(1, Math.max(0, (vh - top) / (height || 1)));
    const shift = new Map();
    const frame = () => {
      raf = 0;
      if (!active()) {
        movers.forEach((m) => { m.style.transform = ''; shift.set(m, 0); });
        if (overlay) overlay.style.height = '';
        return;
      }
      const vh = window.innerHeight / zf(document.documentElement);
      movers.forEach((m) => {
        const h = m.offsetHeight;
        const top = layoutTop(m, shift.get(m) || 0);
        const p = progress(top, h, vh);
        const y = -0.5 * h * (1 - p);
        shift.set(m, y);
        m.style.transform = y ? `translateY(${y.toFixed(2)}px)` : 'translateY(0)';
      });
      if (overlay && panel) {
        const top = layoutTop(panel, shift.get(panel) || 0);
        const p = progress(top, panel.offsetHeight, vh);
        overlay.style.height = `${(100 * (1 - p)).toFixed(2)}%`;
      }
    };
    // 252.0 candidate 10a (3.11.2026): the handler ran on EVERY scroll frame of EVERY page (document capture) and forced a layout per frame
    // (offsetHeight + rect reads after transform writes; 251: Layout 20-48/s on the blog's scroll pass, footer-inova.js:336/:348). Gated: `near`
    // is true only while the footer host is within one viewport of the screen - an IntersectionObserver whose root is the host's own scroll
    // container (a rootMargin on the implicit root is clipped at the scroller's edge on desktop, theme/LEARNED.md 3.11 252.0); far from the
    // footer the scroll event returns at once and the maths above never runs. The gate's own flip requests a frame, so the state is right the
    // moment the footer comes within range. The maths is untouched: what is computed when it runs is what was computed before.
    let near = true;
    const request = () => { if (near && !raf) raf = requestAnimationFrame(frame); };
    if ('IntersectionObserver' in window) {
      let root = host.parentElement;
      while (root && root !== document.body && !(/(auto|scroll)/.test(getComputedStyle(root).overflowY) && root.scrollHeight > root.clientHeight)) root = root.parentElement;
      if (root === document.body) root = null;
      const vh = root ? root.clientHeight : window.innerHeight;
      new IntersectionObserver((es) => { near = es.some((e) => e.isIntersecting); if (near) request(); }, { root, rootMargin: `${vh}px 0px ${vh}px 0px` }).observe(host);
    }
    document.addEventListener('scroll', request, { capture: true, passive: true });
    window.addEventListener('resize', request, { passive: true });
    [mqDesk, mqFine, mqReduce].forEach((mq) => mq.addEventListener('change', request));
    request();
  }

  const boot = () => document.querySelectorAll('[data-finv]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => { const h = e.target.querySelector('[data-finv]'); if (h) init(h); });
})();
